import { StagedEntryState, useTranslation } from "@next-platform/client-common-core";
import { ButtonSize, ButtonVariant, ListItem, ListItemStatusVariant } from "@next-platform/client-common-spicy-ui";
import { LorebookEntry, LorebookEntryStatus } from "@next-platform/packages-ts-types";
import { useCallback, useMemo } from "react";

import Trans from "@/components/Trans/Trans";

import isEntryNeedsFix from "../utils/isEntryNeedsFix";

export type FilteredEntryWithIndex = {
  entry: LorebookEntry;
  originalIndex: number;
};

interface IEntryListItemProps {
  filteredEntry: FilteredEntryWithIndex;
  onOpenModal?: (index?: number) => void;
  isFormDisabled?: boolean;
  hideWarning?: boolean;
  // Staged badge for the public-lorebook staging flow (New / Edited / Removed).
  stagedState?: StagedEntryState;
}

const SUB_LABEL_MAX_LINES = 2;
const EMPTY_KEYWORDS: string[] = [];

function EntryListItem({ filteredEntry, onOpenModal, isFormDisabled, hideWarning, stagedState }: IEntryListItemProps) {
  const handleClick = useCallback(() => {
    onOpenModal?.(filteredEntry.originalIndex);
  }, [onOpenModal, filteredEntry.originalIndex]);
  let isModerated = false;
  const keywords = filteredEntry.entry?.keywords ?? EMPTY_KEYWORDS;
  const { t } = useTranslation();
  // A staged edit prefixes the row label with a marker; a staged removal is also struck through.
  // Inline t() literals (not a keyed map) so the i18n key extractor can find them statically.
  const stagedLabel = useMemo(() => {
    switch (stagedState) {
      case StagedEntryState.NEW:
        return t("lorebook:staged.badge.new");
      case StagedEntryState.EDITED:
        return t("lorebook:staged.badge.edited");
      case StagedEntryState.REMOVED:
        return t("lorebook:staged.badge.removed");
      default:
        return undefined;
    }
  }, [stagedState, t]);
  const isStagedRemoved = stagedState === StagedEntryState.REMOVED;
  let listStatusReason = keywords;
  let listItemStatus = undefined;
  if (hideWarning) {
    listItemStatus = undefined;
  } else if (filteredEntry.entry?.status === LorebookEntryStatus.NEEDS_REVIEW) {
    listStatusReason = [t("lorebook:contentWasTrimmedDuringImport")] as string[];
    isModerated = true;
    listItemStatus = ListItemStatusVariant.NEEDS_REVIEW;
  } else if (isEntryNeedsFix(filteredEntry.entry)) {
    // Prefer the admin's fix_reason when present (public-lorebook needs_fix overlay).
    listStatusReason = [filteredEntry.entry.fix_reason || t("lorebook:moderationRequirementsNotMet")] as string[];
    isModerated = true;
    listItemStatus = ListItemStatusVariant.REJECTED;
  }
  const endAction = useMemo(() => {
    if (isEntryNeedsFix(filteredEntry.entry)) {
      return {
        variant: ButtonVariant.OUTLINED,
        size: ButtonSize.MEDIUM,
        children: <Trans i18nKey="lorebook:review" />,
        className: "pointer-events-none !bg-transparent",
        disabled: isFormDisabled,
      };
    }
    return undefined;
  }, [filteredEntry.entry, isFormDisabled]);

  const label = stagedLabel ? `[${stagedLabel}] ${filteredEntry.entry?.name ?? ""}` : filteredEntry.entry?.name;

  return (
    <ListItem
      status={listItemStatus}
      className={isStagedRemoved ? "min-h-auto opacity-60 line-through" : "min-h-auto"}
      label={label}
      tags={isModerated ? listStatusReason : keywords}
      subLabel={isModerated ? "" : filteredEntry.entry?.content}
      subLabelMaxLines={SUB_LABEL_MAX_LINES}
      showSubLabelTooltip={false}
      onClick={isFormDisabled || isStagedRemoved ? undefined : handleClick}
      endAction={endAction}
    />
  );
}

export default EntryListItem;
